import React from "react";
import { LAB_FIELDS, LAB_FIELD_UNITS } from "./constants";
import { SectionHeader } from "./primitives";
import type { LabField } from "./constants";
import type {
  AdditionalResults,
  AllergyRow,
  CareContext,
  DiagnosisRow,
  FaceSheetOperationSummary,
  ImagingRow,
  LabRow,
  MedicationRow,
  PatientSnapshot,
  ResultRow,
  SourceRef,
} from "./types";
import { formatDate, formatMedicationName, formatPhone, sortLabRowsOldestToNewest, valueOrDash } from "./utils";

interface BaseSectionProps {
  editable: boolean;
  onEdit: () => void;
}

interface OverviewSectionProps extends BaseSectionProps {
  patientSnapshot: PatientSnapshot;
  careContext: CareContext;
}

export function OverviewSection({ patientSnapshot, careContext, editable, onEdit }: OverviewSectionProps) {
  return (
    <article className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
      <SectionHeader kicker="Overview" title="Patient and consult context" editable={editable} onEdit={onEdit} />
      <div className="mt-3 grid gap-2.5 md:grid-cols-2 xl:grid-cols-3">
        <div><p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-400 dark:text-neutral-500">Name</p><p className="mt-0.5 font-semibold">{valueOrDash(patientSnapshot.full_name)}</p></div>
        <div><p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-400 dark:text-neutral-500">DOB / Age</p><p className="mt-0.5 font-semibold">{formatDate(patientSnapshot.date_of_birth)}{patientSnapshot.age ? ` (${patientSnapshot.age})` : ""}</p></div>
        <div><p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-400 dark:text-neutral-500">Care manager</p><p className="mt-0.5 font-semibold">{valueOrDash(careContext.care_manager_name)}</p></div>
        <div><p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-400 dark:text-neutral-500">Email</p><p className="mt-0.5 font-semibold break-all">{valueOrDash(patientSnapshot.email)}</p></div>
        <div><p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-400 dark:text-neutral-500">Cell / Text</p><p className="mt-0.5 font-semibold">{formatPhone(patientSnapshot.phone)}{careContext.text_ok ? ` • Text ${String(careContext.text_ok).toUpperCase()}` : ""}</p></div>
        <div><p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-400 dark:text-neutral-500">City / State</p><p className="mt-0.5 font-semibold">{valueOrDash([patientSnapshot.city, patientSnapshot.state].filter(Boolean).join(", "))}</p></div>
        <div><p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-400 dark:text-neutral-500">Consult date</p><p className="mt-0.5 font-semibold">{formatDate(careContext.consult_date)}</p></div>
        <div><p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-400 dark:text-neutral-500">CPT</p><p className="mt-0.5 font-semibold">{valueOrDash(careContext.cpt_code)}</p></div>
        <div><p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-400 dark:text-neutral-500">Endocrinologist</p><p className="mt-0.5 font-semibold">{valueOrDash(careContext.endocrinologist_name)}</p></div>
      </div>
    </article>
  );
}

interface MedicationsSectionProps extends BaseSectionProps {
  medications: MedicationRow[];
  extractedMedications: MedicationRow[];
  extractedAllergies: AllergyRow[];
  intakeSubmissionTemplate?: string;
  compareDocumentTemplate?: string;
  documentTemplate?: string;
}

const INTAKE_SUBMISSION_PLACEHOLDER = "__INTAKEQ_SUBMISSION_ID__";
const INTAKEQ_MEDICATIONS_SECTION_ANCHOR = "intakeq-section-medications-allergies";

function medicationSourceLabel(medication: MedicationRow): string {
  if (medication.source?.type === "intake_submission") return "IntakeQ";

  return valueOrDash(medication.source?.label || medication.source?.type);
}

function medicationSourceHref(
  medication: MedicationRow,
  intakeSubmissionTemplate?: string,
  compareDocumentTemplate?: string,
  documentTemplate?: string
): string | null {
  if (medication.source?.type === "intake_submission") {
    if (!intakeSubmissionTemplate || medication.source?.id == null) return null;

    return `${intakeSubmissionTemplate.replace(INTAKE_SUBMISSION_PLACEHOLDER, encodeURIComponent(String(medication.source.id)))}#${INTAKEQ_MEDICATIONS_SECTION_ANCHOR}`;
  }

  return documentHref(compareDocumentTemplate, documentTemplate, medication.source);
}

function allergySourceLabel(allergy: AllergyRow): string {
  return valueOrDash(allergy.source?.label || allergy.source?.type);
}

function allergySourceHref(allergy: AllergyRow, compareDocumentTemplate?: string, documentTemplate?: string): string | null {
  return documentHref(compareDocumentTemplate, documentTemplate, allergy.source);
}

export function MedicationsSection({
  medications,
  extractedMedications,
  extractedAllergies,
  editable,
  onEdit,
  intakeSubmissionTemplate,
  compareDocumentTemplate,
  documentTemplate,
}: MedicationsSectionProps) {
  const [showExtractedMedications, setShowExtractedMedications] = React.useState(false);

  const headerActions = extractedMedications.length > 0 ? (
    <button type="button" className="app-button-secondary" onClick={() => setShowExtractedMedications((value) => !value)}>
      {showExtractedMedications ? "Hide extracted meds" : `Show extracted meds (${extractedMedications.length})`}
    </button>
  ) : null;

  return (
    <article className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
      <SectionHeader
        kicker="Medications"
        title="Current medications and supplements"
        editable={editable}
        onEdit={onEdit}
        actions={headerActions}
      />
      {medications.length > 0 ? (
        <div className="mt-3 overflow-x-auto rounded-md border border-neutral-200 dark:border-neutral-800">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
            <thead className="bg-neutral-100/90 dark:bg-neutral-900/80">
              <tr>
                {["Entry", "Dose / frequency", "Source"].map((label) => (
                  <th key={label} className="px-3 py-2 text-left text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {medications.map((medication, index) => {
                const sourceHref = medicationSourceHref(
                  medication,
                  intakeSubmissionTemplate,
                  compareDocumentTemplate,
                  documentTemplate
                );

                return (
                  <tr key={`medication-row-${index}`}>
                    <td className="px-3 py-2 font-semibold text-neutral-950 dark:text-white">{valueOrDash(formatMedicationName(medication))}</td>
                    <td className="px-3 py-2 text-sm text-neutral-600 dark:text-neutral-300">
                      {valueOrDash([medication.dose, medication.frequency].filter(Boolean).join(" • "))}
                    </td>
                    <td className="px-3 py-2 text-sm text-neutral-600 dark:text-neutral-300">
                      {sourceHref ? (
                        <a href={sourceHref} className="font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200">
                          {medicationSourceLabel(medication)}
                        </a>
                      ) : (
                        medicationSourceLabel(medication)
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <p className="mt-3 text-sm text-neutral-500 dark:text-neutral-400">No medications recorded.</p>}

      {extractedAllergies.length > 0 ? (
        <div className="mt-4">
          <p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">Extracted allergies</p>
          <div className="mt-2 overflow-x-auto rounded-md border border-neutral-200 dark:border-neutral-800">
            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
              <thead className="bg-neutral-100/90 dark:bg-neutral-900/80">
                <tr>
                  {["Allergen", "Reaction", "Source"].map((label) => (
                    <th key={label} className="px-3 py-2 text-left text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {extractedAllergies.map((allergy, index) => {
                  const sourceHref = allergySourceHref(allergy, compareDocumentTemplate, documentTemplate);

                  return (
                    <tr key={`allergy-row-${index}`}>
                      <td className="px-3 py-2 font-semibold text-neutral-950 dark:text-white">{valueOrDash(allergy.allergen)}</td>
                      <td className="px-3 py-2 text-sm text-neutral-600 dark:text-neutral-300">{valueOrDash(allergy.reaction)}</td>
                      <td className="px-3 py-2 text-sm text-neutral-600 dark:text-neutral-300">
                        {sourceHref ? (
                          <a href={sourceHref} className="font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200">
                            {allergySourceLabel(allergy)}
                          </a>
                        ) : (
                          allergySourceLabel(allergy)
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {showExtractedMedications ? (
        <div className="mt-4">
          <p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">Extracted medications</p>
          <div className="mt-2 overflow-x-auto rounded-md border border-neutral-200 dark:border-neutral-800">
            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
              <thead className="bg-neutral-100/90 dark:bg-neutral-900/80">
                <tr>
                  {["Entry", "Dose / frequency", "Source"].map((label) => (
                    <th key={label} className="px-3 py-2 text-left text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {extractedMedications.map((medication, index) => {
                  const sourceHref = medicationSourceHref(
                    medication,
                    intakeSubmissionTemplate,
                    compareDocumentTemplate,
                    documentTemplate
                  );

                  return (
                    <tr key={`extracted-medication-row-${index}`}>
                      <td className="px-3 py-2 font-semibold text-neutral-950 dark:text-white">{valueOrDash(formatMedicationName(medication))}</td>
                      <td className="px-3 py-2 text-sm text-neutral-600 dark:text-neutral-300">
                        {valueOrDash([medication.dose, medication.frequency].filter(Boolean).join(" • "))}
                      </td>
                      <td className="px-3 py-2 text-sm text-neutral-600 dark:text-neutral-300">
                        {sourceHref ? (
                          <a href={sourceHref} className="font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200">
                            {medicationSourceLabel(medication)}
                          </a>
                        ) : (
                          medicationSourceLabel(medication)
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </article>
  );
}

interface LabsSectionProps extends BaseSectionProps {
  labs: LabRow[];
  operationSummary?: FaceSheetOperationSummary;
  compareDocumentTemplate?: string;
  documentTemplate?: string;
  openLinksInWindow?: boolean;
  originalFacesheetUrl?: string;
}

const DOCUMENT_PLACEHOLDER = "__DOCUMENT_ID__";

function compareFieldKey(field: LabField): string {
  return field;
}

function labFieldLabel(field: LabField, label: string): string {
  const unit = LAB_FIELD_UNITS[field];
  return unit ? `${label} (${unit})` : label;
}

function labReferenceRange(lab: LabRow, field: LabField): string | undefined {
  if (field === "calcium") return lab.reference_range;
  if (field === "pth") return lab.pth_reference_range;

  return undefined;
}

function documentHref(
  compareDocumentTemplate: string | undefined,
  documentTemplate: string | undefined,
  source: SourceRef | undefined,
  options?: { fieldKey?: string; date?: string; value?: string }
): string | null {
  const documentId = source?.document_id;
  if (documentId == null) return null;

  if (compareDocumentTemplate) {
    const compareHref = compareDocumentTemplate.replace(DOCUMENT_PLACEHOLDER, encodeURIComponent(String(documentId)));
    const sourcePage = Number(source?.source_page);
    const searchParams = new URLSearchParams();

    if (Number.isInteger(sourcePage) && sourcePage > 0) {
      searchParams.set("page", String(sourcePage));
    }
    if (options?.fieldKey) {
      searchParams.set("field_key", options.fieldKey);
    }
    if (options?.date) {
      searchParams.set("result_date", options.date);
    }
    if (options?.value) {
      searchParams.set("result_value", options.value);
    }

    const query = searchParams.toString();
    return query.length > 0 ? `${compareHref}?${query}` : compareHref;

  }

  if (!documentTemplate) return null;

  return `${documentTemplate.replace(DOCUMENT_PLACEHOLDER, encodeURIComponent(String(documentId)))}#document-ai-extraction`;
}

function labFieldSource(lab: LabRow, field: LabField) {
  return lab.field_sources?.[field] || lab.source;
}

function labCellContent(
  lab: LabRow,
  field: LabField,
  compareDocumentTemplate?: string,
  documentTemplate?: string
) {
  const value = lab[field];
  if (!value) return valueOrDash(value);

  const displayValue = field === "date" && typeof value === "string" ? formatDate(value) : value;
  const source = labFieldSource(lab, field);
  const referenceRange = labReferenceRange(lab, field);
  const tooltipTitle = [
    referenceRange ? `Reference range: ${referenceRange}` : null,
    source?.label ? `Source: ${source.label}` : null,
  ].filter(Boolean).join("\n") || undefined;
  const href = documentHref(compareDocumentTemplate, documentTemplate, source, {
    fieldKey: compareFieldKey(field),
    date: lab.date,
    value: typeof value === "string" ? value : undefined,
  });

  if (!href) {
    return tooltipTitle ? <span title={tooltipTitle}>{displayValue}</span> : displayValue;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={tooltipTitle || "Open source document"}
      className="font-inherit text-neutral-950 no-underline hover:text-neutral-950 hover:no-underline dark:text-white dark:hover:text-white"
    >
      {displayValue}
    </a>
  );
}

function sortableDateValue(value?: string | null): number | null {
  if (!value) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return Date.UTC(year, month - 1, day);
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

type LabTimelineEntry =
  | { type: "lab"; date?: string; index: number; lab: LabRow }
  | { type: "operation"; date?: string; operationSummary: FaceSheetOperationSummary };

function sortLabTimeline(entries: LabTimelineEntry[]): LabTimelineEntry[] {
  return [ ...entries ].sort((left, right) => {
    const leftTime = sortableDateValue(left.date);
    const rightTime = sortableDateValue(right.date);

    if (leftTime != null && rightTime != null && leftTime !== rightTime) return leftTime - rightTime;
    if (leftTime != null && rightTime == null) return -1;
    if (leftTime == null && rightTime != null) return 1;

    const leftDate = left.date?.trim() || "";
    const rightDate = right.date?.trim() || "";
    if (leftDate && rightDate && leftDate !== rightDate) return leftDate.localeCompare(rightDate);
    if (leftDate && !rightDate) return -1;
    if (!leftDate && rightDate) return 1;

    if (left.type !== right.type) return left.type === "lab" ? -1 : 1;
    if (left.type === "lab" && right.type === "lab") return left.index - right.index;
    return 0;
  });
}

function operationRowContent(operationSummary: FaceSheetOperationSummary, openLinksInWindow = false) {
  const locationLabel = operationSummary.operationLocationLabel || "Unknown";
  const operationLabel = `Operated at ${locationLabel}`;

  return (
    <span>
      {operationSummary.operationPath ? (
        <a
          href={operationSummary.operationPath}
          target="_blank"
          rel="noreferrer"
          className="font-inherit text-neutral-950 no-underline hover:text-neutral-950 hover:no-underline dark:text-white dark:hover:text-white"
        >
          {operationLabel}
        </a>
      ) : (
        operationLabel
      )}
      {operationSummary.opNotePath ? (
        <>
          {" "}
          -{" "}
          <a
            href={operationSummary.opNotePath}
            target="_blank"
            rel="noreferrer"
            data-open-in-window={openLinksInWindow ? "true" : undefined}
            className="font-inherit text-neutral-950 no-underline hover:text-neutral-950 hover:no-underline dark:text-white dark:hover:text-white"
          >
            Op Note
          </a>
        </>
      ) : null}
    </span>
  );
}

export function LabsSection({
  labs,
  operationSummary,
  editable,
  onEdit,
  compareDocumentTemplate,
  documentTemplate,
  openLinksInWindow = false,
  originalFacesheetUrl,
}: LabsSectionProps) {
  const timelineEntries = sortLabTimeline([
    ...sortLabRowsOldestToNewest(labs).map((lab, index) => ({ type: "lab" as const, date: lab.date, index, lab })),
    ...(operationSummary?.date ? [{ type: "operation" as const, date: operationSummary.date, operationSummary }] : []),
  ]);

  return (
    <article className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
      <SectionHeader
        kicker="Labs"
        title="Lab trend"
        editable={editable}
        onEdit={onEdit}
        actions={
          originalFacesheetUrl ? (
            <a href={originalFacesheetUrl} className="app-button-secondary" target="_blank" rel="noopener noreferrer">
              Open original
            </a>
          ) : undefined
        }
      />
      <div className="mt-3 overflow-x-auto rounded-md border border-neutral-200 dark:border-neutral-800">
        <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
          <thead className="bg-neutral-100/90 dark:bg-neutral-900/80">
            <tr>
              {LAB_FIELDS.map(([field, label]) => (
                <th key={field} className="px-2 py-2 text-left text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                  {labFieldLabel(field, label)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {timelineEntries.length > 0 ? (
              timelineEntries.map((entry, index) => (
                entry.type === "lab" ? (
                  <tr key={`lab-row-${index}`}>
                    {LAB_FIELDS.map(([field]) => (
                      <td key={`${field}-${index}`} className="px-2 py-2">{labCellContent(entry.lab, field as LabField, compareDocumentTemplate, documentTemplate)}</td>
                    ))}
                  </tr>
                ) : (
                  <tr key={`operation-row-${index}`}>
                    <td className="px-2 py-2">{valueOrDash(entry.date ? formatDate(entry.date) : undefined)}</td>
                    <td colSpan={LAB_FIELDS.length - 1} className="px-2 py-2 text-neutral-600 dark:text-neutral-300">
                      {operationRowContent(entry.operationSummary, openLinksInWindow)}
                    </td>
                  </tr>
                )
              ))
            ) : (
              <tr>
                <td colSpan={LAB_FIELDS.length} className="px-3 py-4 text-sm text-neutral-500 dark:text-neutral-400">No lab values have been added yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </article>
  );
}

interface AdditionalResultsSectionProps extends BaseSectionProps {
  additionalResults: AdditionalResults;
  compareDocumentTemplate?: string;
  documentTemplate?: string;
}

function resultFieldKey(resultType: "tsh" | "urine_24h"): string {
  return resultType;
}

function resultTypeLabel(resultType: "tsh" | "urine_24h"): string {
  if (resultType === "tsh") return "TSH (uIU/mL)";
  return "24-hour urine (mg/24 hr)";
}

function resultSummaryContent(
  result: ResultRow,
  resultType: "tsh" | "urine_24h",
  compareDocumentTemplate?: string,
  documentTemplate?: string
) {
  const parts = [result.date ? formatDate(result.date) : "", result.value].filter(Boolean);
  const summary = parts.length > 0 ? parts.join(" • ") : valueOrDash(undefined);
  const title = [
    result.reference_range ? `Reference range: ${result.reference_range}` : null,
    result.source?.label ? `Source: ${result.source.label}` : null,
  ].filter(Boolean).join("\n") || undefined;
  const href = documentHref(compareDocumentTemplate, documentTemplate, result.source, {
    fieldKey: resultFieldKey(resultType),
    date: result.date,
    value: result.value,
  });

  if (!href) return title ? <span title={title}>{summary}</span> : summary;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={title || "Open source document"}
      className="font-inherit text-neutral-950 no-underline hover:text-neutral-950 hover:no-underline dark:text-white dark:hover:text-white"
    >
      {summary}
    </a>
  );
}

export function AdditionalResultsSection({
  additionalResults,
  compareDocumentTemplate,
  documentTemplate,
  editable,
  onEdit,
}: AdditionalResultsSectionProps) {
  return (
    <article className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
      <SectionHeader kicker="Additional results" title="TSH and 24-hour urine" editable={editable} onEdit={onEdit} />
      <div className="mt-3 grid gap-2.5 md:grid-cols-2">
        <article className="rounded-md border border-neutral-200 bg-neutral-50/60 p-3 dark:border-neutral-800 dark:bg-neutral-900/40">
          <p className="font-semibold text-neutral-950 dark:text-white">{resultTypeLabel("tsh")}</p>
          {(additionalResults.tsh || []).length > 0 ? (
            <ul className="mt-1.5 space-y-1">
              {(additionalResults.tsh || []).map((result, index) => (
                <li key={`tsh-${index}`}>{resultSummaryContent(result, "tsh", compareDocumentTemplate, documentTemplate)}</li>
              ))}
            </ul>
          ) : <p className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">No TSH values added.</p>}
        </article>
        <article className="rounded-md border border-neutral-200 bg-neutral-50/60 p-3 dark:border-neutral-800 dark:bg-neutral-900/40">
          <p className="font-semibold text-neutral-950 dark:text-white">{resultTypeLabel("urine_24h")}</p>
          {(additionalResults.urine_24h || []).length > 0 ? (
            <ul className="mt-1.5 space-y-1">
              {(additionalResults.urine_24h || []).map((result, index) => (
                <li key={`urine-${index}`}>{resultSummaryContent(result, "urine_24h", compareDocumentTemplate, documentTemplate)}</li>
              ))}
            </ul>
          ) : <p className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">No 24-hour urine values added.</p>}
        </article>
      </div>
    </article>
  );
}

interface ImagingSectionProps extends BaseSectionProps {
  imaging: ImagingRow[];
  extractedImagingResults: ImagingRow[];
  compareDocumentTemplate?: string;
  documentTemplate?: string;
}

function imagingCellContent(
  image: ImagingRow,
  value: string | undefined,
  compareDocumentTemplate?: string,
  documentTemplate?: string
) {
  if (!value) return valueOrDash(value);

  const title = image.source?.label ? `Source: ${image.source.label}` : undefined;
  const href = documentHref(compareDocumentTemplate, documentTemplate, image.source, {
    date: image.date,
  });

  if (!href) return title ? <span title={title}>{value}</span> : value;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={title || "Open source document"}
      className="font-inherit text-neutral-950 no-underline hover:text-neutral-950 hover:no-underline dark:text-white dark:hover:text-white"
    >
      {value}
    </a>
  );
}

export function ImagingSection({
  imaging,
  extractedImagingResults,
  compareDocumentTemplate,
  documentTemplate,
  editable,
  onEdit,
}: ImagingSectionProps) {
  const [showExtractedImaging, setShowExtractedImaging] = React.useState(false);

  const headerActions = extractedImagingResults.length > 0 ? (
    <button type="button" className="app-button-secondary" onClick={() => setShowExtractedImaging((value) => !value)}>
      {showExtractedImaging ? "Hide extracted imaging" : `Show extracted imaging (${extractedImagingResults.length})`}
    </button>
  ) : null;

  return (
    <article className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
      <SectionHeader kicker="Imaging" title="Imaging studies" editable={editable} onEdit={onEdit} actions={headerActions} />
      <div className="mt-3 overflow-x-auto rounded-md border border-neutral-200 dark:border-neutral-800">
        <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
          <thead className="bg-neutral-100/90 dark:bg-neutral-900/80">
            <tr>
              {["Date", "Image type", "Results"].map((label) => (
                <th key={label} className="px-3 py-2 text-left text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {imaging.length > 0 ? (
              imaging.map((image, index) => (
                <tr key={`imaging-row-${index}`}>
                  <td className="px-3 py-2">
                    {imagingCellContent(image, image.date ? formatDate(image.date) : undefined, compareDocumentTemplate, documentTemplate)}
                  </td>
                  <td className="px-3 py-2">{imagingCellContent(image, image.image_type, compareDocumentTemplate, documentTemplate)}</td>
                  <td className="px-3 py-2">{imagingCellContent(image, image.results, compareDocumentTemplate, documentTemplate)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="px-3 py-4 text-sm text-neutral-500 dark:text-neutral-400">No imaging results have been added yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showExtractedImaging ? (
        <div className="mt-4">
          <p className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">Extracted imaging studies</p>
          <div className="mt-2 overflow-x-auto rounded-md border border-neutral-200 dark:border-neutral-800">
            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
              <thead className="bg-neutral-100/90 dark:bg-neutral-900/80">
                <tr>
                  {["Date", "Image type", "Results"].map((label) => (
                    <th key={label} className="px-3 py-2 text-left text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {extractedImagingResults.map((image, index) => (
                  <tr key={`extracted-imaging-row-${index}`}>
                    <td className="px-3 py-2">
                      {imagingCellContent(image, image.date ? formatDate(image.date) : undefined, compareDocumentTemplate, documentTemplate)}
                    </td>
                    <td className="px-3 py-2">{imagingCellContent(image, image.image_type, compareDocumentTemplate, documentTemplate)}</td>
                    <td className="px-3 py-2">{imagingCellContent(image, image.results, compareDocumentTemplate, documentTemplate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </article>
  );
}

interface DiagnosesSectionProps extends BaseSectionProps {
  diagnoses: DiagnosisRow[];
}

export function DiagnosesSection({ diagnoses, editable, onEdit }: DiagnosesSectionProps) {
  return (
    <article className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
      <SectionHeader kicker="Diagnosis" title="ICD-10 selection" editable={editable} onEdit={onEdit} />
      <div className="mt-3 overflow-x-auto rounded-md border border-neutral-200 dark:border-neutral-800">
        <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
          <thead className="bg-neutral-100/90 dark:bg-neutral-900/80">
            <tr>
              {["Code", "Description", "Status"].map((label) => (
                <th key={label} className="px-3 py-2 text-left text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {diagnoses.length > 0 ? (
              diagnoses.map((diagnosis) => (
                <tr key={diagnosis.code}>
                  <td className="px-3 py-2 font-semibold text-neutral-950 dark:text-white">{valueOrDash(diagnosis.code)}</td>
                  <td className="px-3 py-2 text-sm text-neutral-600 dark:text-neutral-300">{valueOrDash(diagnosis.label)}</td>
                  <td className={`px-3 py-2 text-[0.68rem] font-semibold uppercase tracking-[0.14em] ${diagnosis.selected ? "text-emerald-600 dark:text-emerald-300" : "text-neutral-400 dark:text-neutral-500"}`}>
                    {diagnosis.selected ? "Selected" : "Unselected"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="px-3 py-4 text-sm text-neutral-500 dark:text-neutral-400">No diagnoses have been added yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </article>
  );
}

interface NotesSectionProps extends BaseSectionProps {
  notes?: string;
}

export function NotesSection({ notes, editable, onEdit }: NotesSectionProps) {
  return (
    <article className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
      <SectionHeader kicker="Notes" title="Staff notes" editable={editable} onEdit={onEdit} />
      <div className="mt-3 min-h-32 rounded-md border border-dashed border-neutral-300 p-3 text-sm dark:border-neutral-700">
        {notes ? notes.split("\n").map((line, index) => <p key={`note-${index}`}>{line || "\u00A0"}</p>) : <p>&nbsp;</p>}
      </div>
    </article>
  );
}
